import { useMemo } from "react";
import { CampaignStatus } from "@gen/tracker/v1/campaign_pb";
import { DateRange } from "@gen/tracker/v1/common_pb";
import { DimensionFilterOp, ReportViewDefinition, type ReportView } from "@gen/tracker/v1/reporting_pb";
import { LanderType } from "@gen/tracker/v1/lander_pb";
import type { GroupByDimension, GroupByFold } from "@fox1ck-org/ui";

import { csvEscape, triggerCsvDownload } from "@/lib/csv";
import { derive, type Bag } from "@/lib/campaigns-table";
import { buildTree, flatten, formatFilters, toBag, type DimFilter, type ReportFilters, type ReportNode } from "@/lib/report";
import type { ConversionBasis } from "@/lib/period";
import { useT, type TKey } from "@/lib/i18n";
import { useAllCampaigns } from "@/lib/services/campaign-service";
import { useLandersByIds } from "@/lib/services/lander-service";
import { useReport, type ReportResult } from "@/lib/services/report-service";
import { useReportingSchema, type ReportingSchema } from "@/lib/services/reporting-schema-service";
import { useListReportViews } from "@/lib/services/report-view-service";
import { isBuiltinSet, pickColumns, type ReportColumn } from "./columns";
import { NIL_UUID, rowValues, useDimensionNames } from "./names";
import { useReportState, type ReportDefaults, type ReportState } from "./state";

/**
 * ONE report engine (card У5): «Отчёты», a campaign's «Отчёт» tab, a lander's
 * «Цифры» and an offer's numbers are this hook with a different pin. Every
 * row of every level, «Итого» and the comparison are the server's
 * (GetBreakdownDrilldown with_subtotals); the screen only draws.
 *
 *   const engine = useReportEngine({ defaults: { dims: ["creative", "landing"] },
 *     pinned: [{ key: "campaign", op: DimensionFilterOp.IN, values: [id] }], streamIds: [id] });
 *   <ReportEngine engine={engine} />   — or its parts: ReportGrouping, ReportFilterRow, ReportBody…
 */
export interface ReportEngineConfig {
  /** Where the report starts when the address says nothing. */
  defaults: ReportDefaults;
  /** The opened saved view's definition; the address overrides it. */
  base?: ReportViewDefinition;
  /**
   * Filters the person cannot remove — «Кампания: эта 🔒», «Ленд: этот». They
   * are sent with every query and drawn as a locked chip; they stay out of the
   * address and out of a saved view unless it is saved to «Отчёты».
   */
  pinned?: DimFilter[];
  /**
   * The campaigns read. `"list"` (default) = the campaigns list's own — not
   * archived, «Мои · Все» — so «Итого» here is «Итого» of the list for the same
   * period; an explicit list for a pinned campaign; `[]` for every campaign
   * the caller sees, archive included (a lander's or an offer's numbers).
   */
  streamIds?: "list" | string[];
  /** Dimensions not offered: a campaign's report has no «Кампания» level. */
  hiddenDims?: string[];
}

/** «По гео и часам» is the shape: day of week × hour, optionally under a country. Pure. */
export function isTimeGrid(dims: readonly string[]): boolean {
  const tail = dims.slice(-2);
  return tail[0] === "weekday" && tail[1] === "hour" && (dims.length === 2 || (dims.length === 3 && dims[0] === "country"));
}

/**
 * The picker's sections as the board draws them (доска ReportDims): Кампания ·
 * Источник · Объявление | Гео · Устройство · Время | Воронка · Метки ·
 * Качество. A key the board does not place keeps the schema's own section.
 */
const SECTION: Record<string, string> = {
  campaign: "campaign", group: "campaign", owner: "campaign", entry_point: "campaign", entry_match: "campaign",
  source: "source", ad_campaign: "source",
  creative: "ad", ad_set: "ad", placement: "ad",
  country: "geo", region: "geo", city: "geo", language: "geo",
  device: "device", os: "device", browser: "device", device_model: "device",
  day: "time", hour: "time", weekday: "time", week: "time", month: "time",
  landing: "funnel", offer: "funnel", network: "funnel", flow: "funnel", node: "funnel", branch: "funnel", goal: "funnel", status: "funnel",
  traffic_class: "quality", bot_reason: "quality", isp: "quality", asn: "quality", connection: "quality", ip_subnet: "quality", referrer: "quality", origin: "quality",
};
const sectionOf = (key: string, group: string) => SECTION[key] ?? (/^sub\d+$/.test(key) ? "labels" : group);
/** The sections in reading order: down the first column, then the second, then the third. */
const SECTIONS = ["campaign", "source", "ad", "geo", "device", "time", "funnel", "labels", "quality"];
/**
 * sub1…sub20 are twenty rows of one kind: the picker lists the first
 * `OPEN_SUBS` and folds the rest into one row, «sub3 – sub20», that opens in
 * place (доска ReportDims).
 */
const OPEN_SUBS = 2;
const subN = (key: string) => (/^sub(\d+)$/.test(key) ? Number(key.slice(3)) : 0);

/** The schema's dimensions as the GroupByPicker's: every drilldownable key, in its section. Pure. */
export function groupByDimensions(schema: ReportingSchema | undefined, groupLabel: (g: string) => string, hidden: readonly string[] = []): GroupByDimension[] {
  return (schema?.dimensions ?? [])
    .filter((d) => d.drilldownable && !hidden.includes(d.key))
    .map((d) => ({ value: d.key, label: d.label, group: groupLabel(sectionOf(d.key, d.group)) }));
}

/** The sub-labels past the first `OPEN_SUBS` as one folded row, when there are two or more. Pure. */
export function subFolds(dimensions: readonly GroupByDimension[]): GroupByFold[] {
  const folded = dimensions
    .map((d) => d.value)
    .filter((key) => subN(key) > OPEN_SUBS)
    .sort((a, b) => subN(a) - subN(b));
  if (folded.length < 2) return [];
  return [{ id: "subs", label: `${folded[0]} – ${folded[folded.length - 1]}`, values: folded }];
}

const LANDER_TYPES: Record<string, LanderType> = { landing: LanderType.LANDING, prelanding: LanderType.PRELANDING, vsl: LanderType.VSL };

const LIST_STATUSES = [CampaignStatus.ACTIVE, CampaignStatus.PAUSED, CampaignStatus.DRAFT];

export interface ReportEngine {
  state: ReportState;
  schema: ReportingSchema | undefined;
  pinned: DimFilter[];
  /** What every query is sent: the pin, the person's filters, «Тип» of a lander report. */
  query: ReportFilters;
  hiddenDims: string[];
  dimensions: GroupByDimension[];
  /** sub3 … sub20, folded into one row of the picker. */
  folds: GroupByFold[];
  dimLabel: (key: string) => string;
  /** The picker's section order. */
  groups: string[];
  columns: ReportColumn[];
  /** Saved column sets (`surface=columns`). */
  columnSets: ReportView[];
  time: boolean;
  basis: ConversionBasis;
  /** The comparison period, when «Сравнить» is on. */
  compare: { from: string; to: string } | undefined;
  /** The campaigns read; undefined while the list is loading. */
  streamIds: string[] | undefined;
  main: ReturnType<typeof useReport>;
  daily: ReturnType<typeof useReport>;
  dailyPrev: ReturnType<typeof useReport>;
  tree: ReportNode[];
  totals: Bag | undefined;
  prevTotals: Bag | undefined;
  /** The raw «Итого» row, for «Проверка на текущем отчёте». */
  rawTotals: ReportResult["totals"];
  names: ReturnType<typeof useDimensionNames>;
  /** Nothing to read: the list has no campaigns. */
  noCampaigns: boolean;
  exportCsv: (fileStem?: string) => void;
  /** The open report as a saved view's definition (period only when asked). */
  definition: (opts?: { keepPeriod?: boolean; withPinned?: boolean }) => ReportViewDefinition;
  /** «Открыть в Отчётах»: the same report with the pin as a filter. */
  reportsHref: () => string;
}

export function useReportEngine(config: ReportEngineConfig): ReportEngine {
  const t = useT();
  const schemaQ = useReportingSchema();
  const schema = schemaQ.data;
  const pinned = useMemo(() => config.pinned ?? [], [config.pinned]);
  const hiddenDims = useMemo(() => config.hiddenDims ?? [], [config.hiddenDims]);
  const knownDims = useMemo(() => new Set((schema?.dimensions ?? []).map((d) => d.key)), [schema]);
  const state = useReportState({ defaults: config.defaults, base: config.base, pinned, knownDims });
  const p = state.params;
  const setsQ = useListReportViews("columns");

  // --- which campaigns ------------------------------------------------------------
  const fromList = config.streamIds === undefined || config.streamIds === "list";
  const list = useAllCampaigns(
    { statuses: LIST_STATUSES, mine: p.scope === "mine" },
    { enabled: fromList },
  );
  const streamIds = useMemo(() => {
    if (!fromList) return config.streamIds as string[];
    if (!list.data) return undefined;
    return list.data.items.map((i) => i.campaign?.id ?? "").filter(Boolean);
  }, [fromList, config.streamIds, list.data]);
  // An empty list would read the whole workspace (stream_ids empty = all).
  const noCampaigns = fromList && streamIds !== undefined && streamIds.length === 0;
  const ready = !!schema && streamIds !== undefined && !noCampaigns;

  const time = isTimeGrid(state.dims);
  // An hour says when the person came, not when the partner sent the status.
  const basis: ConversionBasis = time ? "click" : state.basis;
  const compare = time ? undefined : p.compareRange;
  const range = { from: p.from, to: p.to, timezone: p.tz };

  // «Тип: Преленды» — the server has no dimension for a page's kind, so the
  // landers of that kind the period has are asked for by id.
  const typeRows = useReport({ ...range, streamIds, dims: ["landing"], basis, filters: state.query, subtotals: false, limit: 500 }, ready && !!state.landerType);
  const typeLanders = useLandersByIds((typeRows.data?.rows ?? []).map((r) => r.dimensionPath[0] ?? "").filter((v) => v && v !== NIL_UUID), !!state.landerType);
  const wantedType = LANDER_TYPES[state.landerType];
  const typeIds = useMemo(
    () => (wantedType === undefined || !typeLanders.data ? undefined : [...typeLanders.data.values()].filter((l) => l.landerType === wantedType).map((l) => l.id)),
    [wantedType, typeLanders.data],
  );
  const query = useMemo<ReportFilters>(
    () => (wantedType === undefined ? state.query : { ...state.query, dims: [...state.query.dims, { key: "landing", op: DimensionFilterOp.IN, values: typeIds?.length ? typeIds : [NIL_UUID] }] }),
    [state.query, wantedType, typeIds],
  );
  const queryReady = ready && (wantedType === undefined || typeIds !== undefined);

  const main = useReport(
    { ...range, streamIds, dims: time ? state.dims.slice(-2) : state.dims, basis, filters: query, subtotals: true, sort: state.sort, compare },
    queryReady,
  );
  const daily = useReport({ ...range, streamIds, dims: ["day"], basis, filters: query, subtotals: false, limit: 400 }, queryReady);
  // The other period's days, for the band's dashed line: a day of one period
  // is no day of the other, so they line up by position, not by key.
  const dailyPrev = useReport(
    { from: compare?.from ?? "", to: compare?.to ?? "", timezone: p.tz, streamIds, dims: ["day"], basis, filters: query, subtotals: false, limit: 400 },
    queryReady && !time && !!compare,
  );

  const tree = useMemo(() => {
    const roots = buildTree(main.data?.rows ?? []);
    if (!schema) return roots;
    // `derive` fills only what the list derives too (боты %), so a cell reads
    // the same here and on the campaigns list.
    for (const n of flatten(roots)) {
      n.v = derive(schema.metrics, n.v);
      if (n.prev) n.prev = derive(schema.metrics, n.prev);
    }
    return roots;
  }, [main.data, schema]);
  const totals = schema && main.data?.totals ? derive(schema.metrics, toBag(main.data.totals)!) : undefined;
  const prevTotals = schema && main.data?.compareTotals ? derive(schema.metrics, toBag(main.data.compareTotals)!) : undefined;

  const names = useDimensionNames(
    [...rowValues(state.dims, main.data?.rows ?? []), ...[...pinned, ...state.filters.dims].flatMap((f) => f.values.map((v) => [f.key, v] as const))],
    // The campaigns read are the list's: they are named from it, not asked for again.
    fromList ? list.data?.items : undefined,
  );
  const columns = useMemo(() => pickColumns(state.metrics, schema), [state.metrics, schema]);
  const dimLabel = (key: string) => schema?.dimensions.find((d) => d.key === key)?.label ?? key;
  const groupLabel = (g: string) => t(`rep.group.${g}` as TKey);
  const dimensions = useMemo(
    () => groupByDimensions(schema, groupLabel, hiddenDims),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schema, hiddenDims, t],
  );
  const folds = useMemo(() => subFolds(dimensions), [dimensions]);

  const exportCsv = (stem = "report") => {
    const rows = flatten(tree);
    const header = [...state.dims.map(dimLabel), ...columns.filter((c) => c.format !== "outcome").map((c) => (c.custom ? c.label : c.key))];
    const lines = [header.map(csvEscape).join(",")];
    for (const n of rows) {
      const cells = state.dims.map((d, i) => (i <= n.depth ? names.name(d, n.path[i]!) : ""));
      // Raw numbers, not the screen's spelling: a spreadsheet sums them.
      for (const c of columns) if (c.format !== "outcome") cells.push(n.v[c.key] === undefined ? "" : String(n.v[c.key]));
      lines.push(cells.map(csvEscape).join(","));
    }
    if (totals) {
      const cells = state.dims.map((_, i) => (i === 0 ? t("rep.total.csv") : ""));
      for (const c of columns) if (c.format !== "outcome") cells.push(totals[c.key] === undefined ? "" : String(totals[c.key]));
      lines.push(cells.map(csvEscape).join(","));
    }
    triggerCsvDownload(lines.join("\n"), `${stem}-${p.from}-${p.to}.csv`);
  };

  const definition = (opts: { keepPeriod?: boolean; withPinned?: boolean } = {}) => {
    const f: ReportFilters = opts.withPinned ? state.query : state.filters;
    return new ReportViewDefinition({
      surface: "report",
      metricKeys: state.metrics,
      dimensionKeys: state.dims,
      dimensionFilters: f.dims.map((d) => ({ dimensionKey: d.key, op: d.op, values: d.values })),
      metricFilters: f.metrics.map((m) => ({ metricKey: m.key, op: m.op, value: m.value })),
      sortMetricKey: state.sort.key,
      sortDesc: state.sort.desc,
      // The stored view speaks the server's words: prev | year; a hand-picked range is not kept.
      compare: p.compare === "off" ? "" : p.compare === "year" ? "year" : "prev",
      attributionBasis: state.basis === "conv" ? "conversion_time" : state.basis === "click" ? "click_time" : "",
      period: opts.keepPeriod ? new DateRange({ from: p.from, to: p.to }) : undefined,
    });
  };

  const reportsHref = () => {
    const here = new URLSearchParams(window.location.search);
    const sp = new URLSearchParams();
    for (const k of ["preset", "from", "to", "cmp", "by", "who"]) {
      const v = here.get(k);
      if (v) sp.set(k, v);
    }
    sp.set("dims", state.dims.join(","));
    if (state.set) sp.set("cset", state.set);
    if (!isBuiltinSet(state.set)) sp.set("metrics", state.metrics.join(","));
    // The pin becomes an ordinary filter there — removable, like any other.
    sp.set("flt", formatFilters(state.query) || "-");
    return `/stats?${sp.toString()}`;
  };

  return {
    state,
    schema,
    pinned,
    query,
    hiddenDims,
    dimensions,
    folds,
    dimLabel,
    groups: SECTIONS.map(groupLabel),
    columns,
    columnSets: (setsQ.data ?? []).filter((v) => v.scope !== "system"),
    time,
    basis,
    compare,
    streamIds,
    main,
    daily,
    dailyPrev,
    tree,
    totals,
    prevTotals,
    rawTotals: main.data?.totals,
    names,
    noCampaigns,
    exportCsv,
    definition,
    reportsHref,
  };
}

